import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/components/common/toast';
import { clientProfitabilityApi } from '@/api/services/clientProfitability';
import { todayDateString, yesterdayDateString } from '@/api/services/riskDashboard';
import {
  ClientProfitabilityFilterBar,
  defaultCpFilters,
  toCpInput,
  type CpFilterState,
} from './ClientProfitabilityFilterBar';
import { ClientProfitabilityTable } from './ClientProfitabilityTable';
import { ClientProfitabilitySubTables } from './ClientProfitabilitySubTables';
import { FirmPnlChartModal } from './FirmPnlChartModal';
import { LoginOverviewModal } from '@/features/tenant/loginOverview/LoginOverviewModal';
import type { ClientProfitabilityRowDto } from '@/api/types/clientProfitability';
import './clientProfitability.css';

function newGuid(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}`;
}

export function ClientProfitabilityPage() {
  const toast = useToast();
  const [requestGuid] = useState(newGuid);
  const [draft, setDraft] = useState<CpFilterState>(defaultCpFilters);
  const [applied, setApplied] = useState<CpFilterState>(draft);
  const [loModal, setLoModal] = useState<{ login: number; connector: number } | null>(null);
  const [tradesRow, setTradesRow] = useState<ClientProfitabilityRowDto | null>(null);
  const [chartOpen, setChartOpen] = useState(false);

  // Loads directly on open with the default (today) filters — like legacy, no
  // "click Fetch first" gate. Fetch just re-applies the current filters.
  const main = useQuery({
    queryKey: ['cp-main', applied],
    queryFn: () => clientProfitabilityApi.getMain(toCpInput(applied, requestGuid), { live: applied.liveData, byUniqueId: applied.byUniqueId }),
  });

  // Surface the backend warning message (multi-line) as toasts, like legacy.
  const message = main.data?.message;
  useEffect(() => {
    if (message) message.split('\n').filter(Boolean).forEach((m) => toast.info(m));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message]);

  // Fetch re-applies the filters and collapses any open trades sub-tables (Closed /
  // Deals / Open Positions), so stale rows for a previous row aren't left showing.
  const fetch = () => {
    setApplied({ ...draft });
    setTradesRow(null);
  };

  // By Unique ID / Live Data / Show LP are immediate view modes; Live also moves the
  // end date (today on / yesterday off) per FinkitLiveDateSync.
  const onModeToggle = (key: 'byUniqueId' | 'liveData' | 'showLp', value: boolean) => {
    if (key === 'liveData') {
      const newEnd = value ? todayDateString() : yesterdayDateString();
      setDraft((f) => ({ ...f, liveData: value, endDate: newEnd, startDate: f.startDate > newEnd ? newEnd : f.startDate }));
      return;
    }
    if (key === 'byUniqueId') {
      // UID mode clears login/connector/portfolio (hidden) to match legacy.
      setDraft((f) => ({ ...f, byUniqueId: value, login: null, dataSourceId: value ? null : f.dataSourceId, portfolioId: value ? null : f.portfolioId }));
      return;
    }
    setDraft((f) => ({ ...f, [key]: value }));
  };

  const totals = main.data?.totals;
  // Legacy shows integer-truncated, locale-grouped totals; N/A when absent.
  const money = (v: number | null | undefined) => (v == null ? 'N/A' : Math.trunc(v).toLocaleString());

  return (
    <div className="client-profitability-page">
      <PageHeader title="Client Profitability" breadcrumb={['Home', 'Client Profitability']} />

      <ClientProfitabilityFilterBar
        draft={draft}
        setDraft={setDraft}
        onFetch={fetch}
        onModeToggle={onModeToggle}
        fetching={main.isFetching}
      />

      {/* Summary widgets — always visible, N/A until fetched (mirrors _SummaryWidgets). */}
      <div id="summary-counters" className="row g-2 mb-2">
        <Widget label="Firm Net PnL" value={money(totals?.firmTotalPnl)} onViewGraph={() => setChartOpen(true)} />
        <Widget label="Clients PnL" value={money(totals?.clientsTotalPnl)} />
        <Widget label="LP PnL" value={money(totals?.lpTotalPnl)} />
      </div>

      {/* Main table — only the active Live×UID variant; shown directly on open. */}
      <ClientProfitabilityTable
        rows={main.data?.items ?? []}
        live={applied.liveData}
        byUniqueId={applied.byUniqueId}
        loading={main.isFetching}
        onShowTrades={(r) => setTradesRow(r)}
        onOpenLogin={(login, platformId) => setLoModal({ login, connector: platformId })}
      />

      {tradesRow && <ClientProfitabilitySubTables row={tradesRow} applied={applied} requestGuid={requestGuid} />}

      <FirmPnlChartModal open={chartOpen} onClose={() => setChartOpen(false)} filters={toCpInput(applied, requestGuid)} />
      {loModal && <LoginOverviewModal login={loModal.login} connector={loModal.connector} onClose={() => setLoModal(null)} />}
    </div>
  );
}

// Summary KPI card — matches _SummaryWidgets.cshtml (money-dollar icon, box-shadow-info,
// optional "View Graph" link on the Firm card).
function Widget({ label, value, onViewGraph }: { label: string; value: string; onViewGraph?: () => void }) {
  return (
    <div className="col-lg-4 col-sm-12">
      <div className="card custom-card overflow-hidden mb-0">
        <div className="card-body">
          <div className="row">
            <div className="col">
              <h3 className="mb-2 fw-semibold">{value}</h3>
              <p className="text-muted mb-0 fs-14 text-nowrap">{label}</p>
            </div>
            <div className="col col-auto d-flex align-items-center">
              {onViewGraph && (
                <a href="#" className="text-primary me-2 text-nowrap" onClick={(e) => { e.preventDefault(); onViewGraph(); }}>
                  View Graph
                </a>
              )}
              <span className="ms-auto bg-primary rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: 48, height: 48 }}>
                <i className="text-white ri-money-dollar-box-line fs-5" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
